import * as React from "react";
import { Link } from "react-router-dom";

import "./setup-modal.scss";

import {cs}              from "@Common/react/chain-services";
import {fragments}       from "@Common/react/fragments";
import {Invoke}          from "@Common/react/invoke";
import {keyed}           from "@Common/react/keyed";
import {UseState}        from "@Common/react/use-state";
import {Radio}           from "@Common/ui-components/heavy/common/design2/radio/radio";
import {le, today}       from "@Common/utils/date-object";
import {parseDateFormat} from "@Common/utils/date/parse-date-format";
import {omit}            from "@Common/utils/objects";

import {EnrollmentSteps} from "../steps/enrollment-steps";

import {InplaceModalService} from "./inplace-modal-service";
import {SetupWizard}         from "./setup-wizard";

export const SetupModal = (
    {
        onShowStep,
        onShowImportingPage=undefined,
        saveEnrollment,
        getStyleTemplate,
        program, enrollment, agentId, branchId, initShow,
        setNavBlock,
        next: rootNext,
    }
) => cs(
    ["modal", ({}, next) => InplaceModalService({
        // initShow: initShow ?? isDevQuan(),
        initShow,
        // header: {noBorder: true},
        title: `${program.programPreferences.linkName} Setup`,
        // width: 673,
        // persistentRender,
        xdQaName: "Scheduled Campaigns Setup Wizard",
        next,
        rootNext,
    })],

    ["saveEnrollment", ({modal}, next) => next(async (newValue, options) => {
        return await saveEnrollment(newValue, {...options, params: modal.params, inSetupModal: true});
    })],
    ({modal, saveEnrollment}, next) => enrollment?.unenroll_reason ? fragments(
        ImportingEnrollmentData({
            enrollment,
            program,
            saveEnrollment,
            onCancel: () => modal.close(),
        }),
        onShowImportingPage && Invoke({
            action: () => onShowImportingPage(modal.params),
        })
    ) : (
        next()
    ),

    ["steps", (_, next) => EnrollmentSteps({
        initEnrollment: enrollment,
        getStyleTemplate,
        agentId, branchId, program,
        next,
    })],

    ({modal, steps, saveEnrollment}) => SetupWizard({
        steps, enrollment,
        saveEnrollment,

        initTab: modal.params?.tab,
        setNavBlock,
        onClose: () => modal.close(),
        onShowStep: (stepIndex) => onShowStep(stepIndex, modal.params),
    }),

);

const ImportingEnrollmentData = ({enrollment, program, saveEnrollment, onCancel}) => cs(
    ["choice", (_, next) => UseState({initValue: "import", next})],
    ({choice}) => (
        <div className="importing-enrollment-data-4rk">
            <div className="content">
                <div className="message">
                    It appears that you were previously enrolled in <b>{program.programPreferences.linkName}</b>. We're glad to have you back! To ensure a seamless experience, please choose how you would like to proceed:
                </div>

                <div className="choices">
                    {[
                        {
                            value: "import",
                            label: <>
                                <b>Import previous settings</b>:{" "}
                                By selecting this option, we will pull in your default settings & preferences from your previous enrollment. You will still have the option to edit those settings. Please note that any Editions that have already passed will not be included. You can review the history of previously completed Editions through your{" "}
                                <Link {...{
                                    to: "/account/orders/history",

                                    "xd-qa-type": "link",
                                    "xd-qa-name": "Order History",
                                }}>Order History</Link>.
                            </>,
                            xdQaName: "Import previous settings",
                        },
                        {
                            value: "create_new",
                            label: <><b>Start over</b>: If you prefer to start fresh and set new preferences, select this option. You will have the opportunity to customize your settings according to your current preferences.</>,
                            xdQaName: "Start over",
                        },
                    ]
                        .map((c) => cs(
                            keyed(c.value),
                            () => (
                                <div
                                    className="choice"
                                    onClick={() => choice.onChange(c.value)}
                                >
                                    <div className="radio">
                                        {Radio({
                                            selected: c.value === choice.value,
                                            xdQaName: c.xdQaName,
                                        })}
                                    </div>

                                    <div className="label">
                                        {c.label}
                                    </div>
                                </div>
                            ),
                        ))
                    }
                </div>

                <div className="message">
                    Please select your preferred option to proceed with the enrollment process.
                </div>
            </div>

            <div className="footer">
                <button {...{
                    className: "cancel",
                    onClick: onCancel,
                    "xd-qa-type": "button",
                    "xd-qa-name": "Cancel",
                }}>
                    Cancel
                </button>

                <button {...{
                    className: "next",
                    disabled: !choice.value,
                    onClick: async () => {
                        if (choice.value === "import") {
                            await saveEnrollment({
                                ...omit(enrollment, ["_id", "enrolled", "unenroll_reason", "unenroll_time"]),
                                editionsPreferences: enrollment.editionsPreferences.filter((ue) => {
                                    const pe = program["programPreferences"].editions.find((s1) => s1.editionId === ue.editionId);
                                    return pe && le(
                                        today(),
                                        parseDate(pe.enrollmentDeadline),
                                    );
                                }),
                            });
                        } else { // create_new
                            await saveEnrollment(null);
                        }

                    },
                    "xd-qa-type": "button",
                    "xd-qa-name": "Next",
                }}>
                    Next
                </button>
            </div>
        </div>
    )
);

const parseDate = (str):any => parseDateFormat("yyyy-mm-dd").parseDate(str);

